import React, { useState, useEffect } from 'react';
import { Table, Card, Btn, Modal, Input, Select, Textarea, FormGrid, SectionHeader, Badge, StatCard, NeedProject, RNLLoader } from '../components/UI';
import { fmt, fmtNum, fmtDate, statusColor, exportCSV } from '../utils';
import * as api from '../api';
import { useParams, useNavigate } from 'react-router-dom';


// ─── SITE ISSUES ─────────────────────────────
function SiteIssuesTab({ projectId, toast }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);
  const [form, setForm]       = useState({});
  const [saving, setSaving]   = useState(false);

  const [subsections, setSubsections] = useState([]);

  const load = async () => {
    setLoading(true);
    try { 
      const [d, subs] = await Promise.all([
        api.listSiteIssues(projectId),
        api.listDmrSubsections(projectId).catch(() => [])
      ]);
      setRows(Array.isArray(d) ? d : []); 
      setSubsections(Array.isArray(subs) ? subs : []);
    }
    catch {} setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [projectId]);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (form.site_issue_id) await api.updateSiteIssue(form.site_issue_id, form);
      else await api.createSiteIssue({ ...form, project_id: projectId });
      toast.show('Saved'); setModal(null); load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  const openCount     = rows.filter(r => r.status === 'open').length;
  const inProgCount   = rows.filter(r => r.status === 'in_progress').length;
  const criticalCount = rows.filter(r => r.priority === 'critical').length;
  const resolvedCount = rows.filter(r => r.status === 'resolved').length;

  return (
    <>
      {/* KPI row */}
      <div className="grid-cols-4" style={{ gap: 12, marginBottom: 16 }}>
        <StatCard label="Open"       value={openCount}     color="red"   />
        <StatCard label="In Progress"value={inProgCount}   color="amber" />
        <StatCard label="Critical"   value={criticalCount} color={criticalCount > 0 ? 'red' : 'green'} />
        <StatCard label="Resolved"   value={resolvedCount} color="green" />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 14 }}>
        <Btn variant="ghost" onClick={() => exportCSV(rows, 'site-issues')}>⬇ CSV</Btn>
        <Btn onClick={() => { setForm({ priority: 'medium', reported_date: new Date().toISOString().slice(0, 10), time_impact_days: 0 }); setModal('form'); }}>+ Report Issue</Btn>
      </div>

      <Card>
        <Table loading={loading} cols={[
          { key: 'issue_number',           label: 'Issue #',    render: v => <span style={{ fontFamily: 'var(--mono)', color: 'var(--red)' }}>{v}</span> },
          { key: 'title',                  label: 'Title' },
          { key: 'location',               label: 'Location',   render: v => <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{v || '—'}</span> },
          { key: 'priority',               label: 'Priority',   render: v => <Badge color={statusColor(v)}>{v}</Badge> },
          { key: 'status',                 label: 'Status',     render: v => <Badge color={statusColor(v)}>{v}</Badge> },
          { key: 'reported_date',          label: 'Reported',   render: v => fmtDate(v) },
          { key: 'estimated_cost_impact',  label: 'Cost Impact',render: v => v ? fmt(v) : '—' },
          { key: 'time_impact_days',       label: 'Days',       render: v => v ? `${v}d` : '—' },
          { key: 'site_issue_id',          label: '',           render: (id, row) => (
            <div style={{ display: 'flex', gap: 6 }}>
              <Btn variant="ghost"  size="sm" style={{ padding: '3px 8px' }} onClick={() => { setForm({ ...row }); setModal('form'); }}>Edit</Btn>
              <Btn variant="danger" size="sm" style={{ padding: '3px 8px' }} onClick={async () => {
                if (window.confirm('Delete this issue?')) try { await api.deleteSiteIssue(id); toast.show('Deleted'); load(); } catch (e) { toast.show(e.message, 'error'); }
              }}>Del</Btn>
            </div>
          )},
        ]} rows={rows} />
      </Card>

      {modal === 'form' && (
        <Modal title={form.site_issue_id ? 'Update Site Issue' : 'Report Site Issue'} onClose={() => setModal(null)} width={680}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FormGrid>
              <Input label="Issue Number"  value={form.issue_number || ''} onChange={set('issue_number')} placeholder="SI-001" />
              <Input label="Title" required value={form.title || ''} onChange={set('title')} />
              <Select label="Sub-section" value={form.dmr_subsection_id || ''} onChange={set('dmr_subsection_id')}
                options={[{ value: '', label: '— Select Sub-section —' }, ...subsections.map(s => ({ value: s.id, label: s.name }))]} />
              <Input label="Location"      value={form.location || ''} onChange={set('location')} />
              <Input label="Reported Date" type="date" value={form.reported_date || ''} onChange={set('reported_date')} />
              <Select label="Priority" value={form.priority || 'medium'} onChange={set('priority')}
                options={['low', 'medium', 'high', 'critical'].map(s => ({ value: s, label: s }))} />
              <Select label="Status" value={form.status || 'open'} onChange={set('status')}
                options={['open', 'in_progress', 'resolved', 'closed'].map(s => ({ value: s, label: s }))} />
              <Input label="Est. Cost Impact"   type="number" value={form.estimated_cost_impact || ''} onChange={set('estimated_cost_impact')} />
              <Input label="Time Impact (days)" type="number" value={form.time_impact_days || '0'} onChange={v => setForm(f => ({ ...f, time_impact_days: parseInt(v) || 0 }))} />
            </FormGrid>
            <Textarea label="Description"     value={form.description || ''} onChange={set('description')} rows={2} />
            <Textarea label="Resolution Notes" value={form.resolution_notes || ''} onChange={set('resolution_notes')} rows={2} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
              <Btn onClick={save} loading={saving}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── MAIN ─────────────────────────────────────
export default function SiteExecutionPage({ toast }) {
  const navigate = useNavigate();
  const { projectId, tab } = useParams();

  const [loading, setLoading] = useState(true);
  const [issuesCount, setIssuesCount] = useState({ total: 0, open: 0, resolved: 0 });

  useEffect(() => {
    if (!projectId || tab) return;
    const load = async () => {
      setLoading(true);
      try {
        const issues = await api.listSiteIssues(projectId).catch(() => []);
        setIssuesCount({
          total: issues.length,
          open: issues.filter(i => i.status === 'open').length,
          resolved: issues.filter(i => i.status === 'resolved').length
        });
      } catch (e) {}
      setLoading(false);
    };
    load();
  }, [projectId, tab]);

  if (!projectId) return <NeedProject />;

  return (
    <div className="fade-in">
      {!tab && (
        <>
          <SectionHeader title="Site Issues" subtitle="Overview" />
          
          {loading ? (
            <RNLLoader title="Loading Issues..." subtitle="Please wait" />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 24 }}>
              <StatCard label="Total Site Issues" value={issuesCount.total} color="amber" icon="⚡" />
              <StatCard label="Open Issues" value={issuesCount.open} color="red" icon="⚠" />
              <StatCard label="Resolved Issues" value={issuesCount.resolved} color="green" icon="✓" />
            </div>
          )}

          {/* DIVIDER */}
          <hr style={{ border: 'none', borderTop: '3px solid #94a3b8', margin: '0 0 32px 0' }} />

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <Card
              className="toggle-on-hover"
              onClick={() => navigate(`/projects/${projectId}/site-issues/issues`)}
              style={{ width: 280, padding: 24, cursor: 'pointer', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, border: '1px solid black' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'black'}
            >
              <span style={{ fontSize: 32, padding: 12, background: 'var(--accent-dim)', borderRadius: 16 }}>⚡</span>
              <span style={{ fontWeight: 700, fontSize: 16 }}>Site Issues</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Track and manage site execution problems.</span>
            </Card>
          </div>
        </>
      )}

      {tab && (
        <>
          <SectionHeader title="Site Issues" subtitle="Site Issues" />
          {tab === 'issues' && <SiteIssuesTab projectId={projectId} toast={toast} />}
        </>
      )}
    </div>
  );
}
